import type { SessionDescriptionHandlerModifier } from 'sip.js';

/**
 * SDP modifier that prioritizes Opus codec for audio.
 * Opus provides better quality at lower bitrates for VoIP.
 */
export const preferOpusModifier: SessionDescriptionHandlerModifier = (
  description: RTCSessionDescriptionInit
): Promise<RTCSessionDescriptionInit> => {
  if (!description.sdp) {
    return Promise.resolve(description);
  }

  const sdpLines = description.sdp.split('\r\n');
  const mLineIndex = sdpLines.findIndex(line => line.startsWith('m=audio'));

  if (mLineIndex === -1) {
    return Promise.resolve(description);
  }

  // Find Opus payload type
  const opusPayloadType = sdpLines
    .slice(mLineIndex)
    .find(line => line.includes('opus/48000'))
    ?.match(/a=rtpmap:(\d+) opus/)?.[1];

  if (!opusPayloadType) {
    return Promise.resolve(description);
  }

  // Move Opus to the front of the codec list in m= line
  const mLine = sdpLines[mLineIndex];
  const parts = mLine.split(' ');
  const codecs = parts.slice(3); // Skip 'm=audio <port> <protocol>'

  const reordered = [
    opusPayloadType,
    ...codecs.filter(c => c !== opusPayloadType),
  ];

  sdpLines[mLineIndex] = `${parts.slice(0, 3).join(' ')} ${reordered.join(' ')}`;

  return Promise.resolve({
    ...description,
    sdp: sdpLines.join('\r\n'),
  });
};
